<template>
    <!-- Новий дизайн (?v2): випадаючий список замість нативного select — як у новій адмінці.
         v-model — значення вибраного пункту; change — після вибору (те саме значення).
         Панель position: fixed, щоб її не обрізала прокрутка бокової панелі кошика -->
    <div class="bp-select" :class="{ 'is-open': open, 'is-invalid': invalid, 'has-icon': !!icon }">
        <button type="button" class="bp-select__btn" ref="btn" :id="id"
                aria-haspopup="listbox" :aria-expanded="open ? 'true' : 'false'" :aria-label="ariaLabel || placeholder"
                @click="toggle()" @keydown="onButtonKey">
            <span v-if="icon" class="bp-select__icon" v-html="icon"></span>
            <span class="bp-select__value" :class="{ 'is-placeholder': !selected }">{{ selected ? selected.label : placeholder }}</span>
            <span class="bp-select__chev" v-html="icons.chevron"></span>
        </button>

        <div v-if="open" class="bp-select__panel" ref="panel" :style="panelStyle" @mousedown.prevent>
            <div v-if="withSearch" class="bp-select__search">
                <span class="bp-select__search-icon" v-html="icons.search"></span>
                <input ref="search" type="text" v-model="query" class="bp-select__search-input" :placeholder="searchPlaceholder"
                       autocomplete="off" aria-label="Пошук у списку" :aria-activedescendant="activeId"
                       @keydown="onListKey" @mousedown.stop>
            </div>
            <ul class="bp-select__list" ref="list" role="listbox" :aria-label="ariaLabel || placeholder" :style="{ maxHeight: listMax + 'px' }">
                <li v-for="(option, index) in filtered" :key="String(option.value)" :id="uid + '-' + index"
                    role="option" class="bp-select__option"
                    :class="{ 'is-selected': isSelected(option), 'is-active': index == cursor, 'is-disabled': option.disabled }"
                    :aria-selected="isSelected(option) ? 'true' : 'false'" :aria-disabled="option.disabled ? 'true' : null"
                    @mousemove="cursor = index" @click="choose(option)">
                    <span v-if="option.icon" class="bp-select__opt-ic" v-html="option.icon"></span>
                    <span class="bp-select__label">{{ option.label }}</span>
                    <span v-if="isSelected(option)" class="bp-select__check" v-html="icons.check"></span>
                </li>
                <li v-if="!filtered.length" class="bp-select__empty">Нічого не знайдено</li>
            </ul>
        </div>
    </div>
</template>

<script>
import { ICONS } from '@/v2icons'

let uidCounter = 0;

export default {
    name: 'BpSelect',
    props: {
        value: null,
        // [{ value, label, disabled, icon }] — icon: необов'язковий рядок SVG перед підписом
        options: { type: Array, default: () => [] },
        placeholder: { type: String, default: '' },
        ariaLabel: String,
        // Поле пошуку: true / false; без значення — коли пунктів більше 8
        searchable: { type: Boolean, default: undefined },
        searchPlaceholder: { type: String, default: 'Пошук' },
        invalid: Boolean,
        // Контурна іконка зліва в полі (рядок SVG із src/v2icons.js)
        icon: String,
        id: String
    },
    data() {
        return {
            icons: ICONS,
            uid: 'bp-sel-' + (++uidCounter),
            open: false,
            query: '',
            cursor: -1,
            panelStyle: {},
            listMax: 320
        }
    },
    computed: {
        selected() {
            return this.options.find(option => this.isSelected(option)) || null;
        },
        withSearch() {
            return this.searchable === undefined ? this.options.length > 8 : this.searchable;
        },
        // Без урахування регістру; збіг з початку назви — вище за збіг усередині
        filtered() {
            const q = this.query.trim().toLowerCase();
            if (!q) return this.options;
            const starts = [], inside = [];
            this.options.forEach(option => {
                const label = String(option.label).toLowerCase();
                if (label.startsWith(q)) starts.push(option);
                else if (label.includes(q)) inside.push(option);
            });
            return starts.concat(inside);
        },
        activeId() {
            return this.cursor >= 0 ? this.uid + '-' + this.cursor : null;
        }
    },
    watch: {
        query() {
            this.cursor = this.firstEnabled(0, 1);
        }
    },
    beforeDestroy() {
        this.unbind();
    },
    methods: {
        isSelected(option) {
            // Як у нативного select з v-model: null у моделі вибирає пункт зі значенням null
            return option.value === this.value || (option.value !== null && this.value !== null && String(option.value) === String(this.value));
        },

        toggle() {
            this.open ? this.close(true) : this.show();
        },

        show() {
            if (this.open) return;
            this.open = true;
            this.query = '';
            const index = this.filtered.findIndex(option => this.isSelected(option));
            this.cursor = index >= 0 ? index : this.firstEnabled(0, 1);
            // pointerdown, а не mousedown: банер (Swiper) скасовує pointerdown, і mousedown тоді не приходить
            document.addEventListener('pointerdown', this.onOutside, true);
            window.addEventListener('resize', this.place);
            window.addEventListener('scroll', this.onScroll, true);
            this.$nextTick(() => {
                this.place();
                if (this.withSearch && this.$refs.search) this.$refs.search.focus({ preventScroll: true });
                this.scrollToCursor();
            });
        },

        close(focusButton) {
            if (!this.open) return;
            this.open = false;
            this.unbind();
            if (focusButton && this.$refs.btn) this.$refs.btn.focus({ preventScroll: true });
        },

        unbind() {
            document.removeEventListener('pointerdown', this.onOutside, true);
            window.removeEventListener('resize', this.place);
            window.removeEventListener('scroll', this.onScroll, true);
        },

        onOutside(event) {
            if (!this.$el.contains(event.target)) this.close(false);
        },

        // Гортається сторінка чи панель кошика — панель їде за полем; список гортається сам — нічого
        onScroll(event) {
            if (this.$refs.list && event.target === this.$refs.list) return;
            this.place();
        },

        // Під полем, якщо там вистачає місця, інакше над ним; висота — не більше видимої частини екрана
        place() {
            if (!this.open || !this.$refs.btn) return;
            const rect = this.$refs.btn.getBoundingClientRect();
            const view = window.visualViewport ? window.visualViewport.height : window.innerHeight;
            // На телефоні знизу — меню категорій (70 px)
            const bottomBar = window.innerWidth <= 1024 ? 70 : 0;
            const gap = 6, edge = 8;
            const searchH = this.withSearch ? 52 : 0;
            const below = view - bottomBar - rect.bottom - gap - edge;
            const above = rect.top - gap - edge;
            const want = Math.min(320, this.options.length * 40 + 8) + searchH;
            const up = below < Math.min(want, 200) && above > below;
            const room = Math.max(120, up ? above : below);
            this.listMax = Math.max(80, Math.min(320, room - searchH));
            const style = {
                left: Math.round(rect.left) + 'px',
                width: Math.round(rect.width) + 'px'
            };
            if (up) style.bottom = Math.round(view - rect.top + gap) + 'px';
            else style.top = Math.round(rect.bottom + gap) + 'px';
            this.panelStyle = style;
        },

        choose(option) {
            if (!option || option.disabled) return;
            this.$emit('input', option.value);
            this.$emit('change', option.value);
            this.close(true);
        },

        firstEnabled(from, step) {
            const list = this.filtered;
            for (let i = from; i >= 0 && i < list.length; i += step) {
                if (!list[i].disabled) return i;
            }
            return -1;
        },

        move(step) {
            const next = this.firstEnabled(this.cursor + step, step);
            if (next != -1) this.cursor = next;
            this.scrollToCursor();
        },

        scrollToCursor() {
            this.$nextTick(() => {
                const list = this.$refs.list;
                const el = list && this.cursor >= 0 ? list.children[this.cursor] : null;
                if (!el) return;
                if (el.offsetTop < list.scrollTop) list.scrollTop = el.offsetTop;
                else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight;
            });
        },

        onButtonKey(event) {
            if (!this.open) {
                if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
                    event.preventDefault();
                    this.show();
                }
                return;
            }
            this.onListKey(event);
        },

        onListKey(event) {
            if (event.key == 'ArrowDown') { event.preventDefault(); this.move(1); }
            else if (event.key == 'ArrowUp') { event.preventDefault(); this.move(-1); }
            else if (event.key == 'Enter' || (event.key == ' ' && !this.withSearch)) { event.preventDefault(); this.choose(this.filtered[this.cursor]); }
            else if (event.key == 'Escape') { event.preventDefault(); this.close(true); }
            else if (event.key == 'Tab') { this.close(false); }
        }
    }
}
</script>
